import { EMPTY_DASH } from "@components/readOnlyList/ReadOnlyList";
import { IFieldDef } from "@components/smart/FieldInfo";
import { createPath } from "@odata/BindingContext";
import {
    IPrPayslipEmploymentEntity,
    IPrPayslipEmploymentItemEntity,
    IPrPayslipEmploymentSubitemEntity,
    IPrPayslipEntity,
    IPrPayslipItemEntity,
    IPrPayslipSubitemEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEmploymentItemEntity,
    PrPayslipEmploymentSubitemEntity,
    PrPayslipEntity,
    PrPayslipItemEntity,
    PrPayslipItemTypeEntity,
    PrPayslipSubitemEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrPayslipEmploymentItemTypeCode,
    PrPayslipEmploymentSubitemTypeCode,
    PrPayslipItemTypeCode,
    PrPayslipSubitemTypeCode
} from "@odata/GeneratedEnums";
import {
    IEditableInfoDef,
    IFormTableListAdditionalColumn,
    IFormTableListRow,
    IFormTableListSection,
    TFormTableListValue
} from "@pages/payroll/payslip/formTableList/FormTableList";
import {
    getTotalOverviewEmploymentSectionsDef,
    getTotalOverviewPayslipSectionsDef
} from "@pages/payroll/payslip/overview/TotalOverview.def";
import {
    dayFormatter,
    getEmploymentName,
    hoursFormatter,
    minutesToHoursFormatter
} from "@pages/payroll/payslip/Payslip.utils";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { formatPercent } from "@utils/general";

import CurrencyType, { formatCurrency } from "../../../../types/Currency";
import { FormStorage } from "../../../../views/formView/FormStorage";

export enum RowValueType {
    Currency,
    Percentage,
    Days,
    Hours,
    HoursFromMinutes,
    CurrencyPerHour
}

export type TPayslipItem = IPrPayslipItemEntity | IPrPayslipEmploymentItemEntity;
export type TPayslipSubItem = IPrPayslipSubitemEntity | IPrPayslipEmploymentSubitemEntity;
export type TPayslipItemType = PrPayslipItemTypeCode | PrPayslipEmploymentItemTypeCode | PrPayslipSubitemTypeCode | PrPayslipEmploymentSubitemTypeCode;

export type TPayslipSubItemDef = Omit<IPayslipOverviewEditableRowDef, "subItems"> | Omit<IPayslipOverviewReadOnlyRowDef, "subItems">;
// common config props
interface IPayslipOverviewRowDef {
    isHeader?: boolean;
    isSubRow?: boolean;
    label?: string;
    visibleIfContains?: string[];
    subItems?: TPayslipSubItemDef[];
    valueType?: RowValueType;
    formatter?: (item: TPayslipItem, valueTypeFormatter: ((value: number) => string), value: TFormTableListValue) => string | React.ReactElement;
}

interface IPayslipOverviewEditableRowDef extends IPayslipOverviewRowDef {
    id?: TPayslipItemType | TPayslipItemType[];
    headerRow?: Omit<TPayslipOverviewRowDef, "headerRow">;
}

interface IPayslipOverviewReadOnlyRowDef extends IPayslipOverviewRowDef {
    value?: number | string;
}

export type TPayslipOverviewRowDef = IPayslipOverviewEditableRowDef | IPayslipOverviewReadOnlyRowDef;

function isEditableRowDef(rowDef: TPayslipOverviewRowDef): rowDef is IPayslipOverviewEditableRowDef {
    return !!(rowDef as IPayslipOverviewEditableRowDef).id;
}

/**
 * Get additional properties for form definition
 */
export function getTotalOverviewEmploymentAdditionalProperties(): IFieldDef[] {
    return [{
        id: PrPayslipEmploymentEntity.Items,
        additionalProperties: [
            { id: PrPayslipEmploymentItemEntity.Name },
            { id: PrPayslipEmploymentItemEntity.TypeCode },
            { id: createPath(PrPayslipEmploymentItemEntity.Type, PrPayslipItemTypeEntity.Name) },
            { id: PrPayslipEmploymentItemEntity.DecimalValue },
            { id: PrPayslipEmploymentItemEntity.DecimalValueOverride },
            { id: PrPayslipEmploymentItemEntity.IntValue },
            { id: PrPayslipEmploymentItemEntity.IntValueOverride },
            { id: PrPayslipEmploymentItemEntity.Percentage },
            {
                id: PrPayslipEmploymentItemEntity.Subitems,
                additionalProperties: [
                    { id: PrPayslipEmploymentSubitemEntity.TypeCode },
                    { id: createPath(PrPayslipEmploymentSubitemEntity.Type, PrPayslipItemTypeEntity.Name) },
                    { id: PrPayslipEmploymentSubitemEntity.DecimalValue },
                    { id: PrPayslipEmploymentSubitemEntity.IntValue },
                ]
            },
        ]
    }];
}
export function getTotalOverviewAdditionalProperties(): IFieldDef[] {
    return [{
        id: PrPayslipEntity.Items,
        additionalProperties: [
            { id: PrPayslipItemEntity.Name },
            { id: PrPayslipItemEntity.TypeCode },
            { id: createPath(PrPayslipItemEntity.Type, PrPayslipItemTypeEntity.Name) },
            { id: PrPayslipItemEntity.DecimalValue },
            { id: PrPayslipItemEntity.DecimalValueOverride },
            {
                id: PrPayslipItemEntity.Subitems,
                additionalProperties: [
                    { id: PrPayslipSubitemEntity.TypeCode },
                    { id: createPath(PrPayslipSubitemEntity.Type, PrPayslipItemTypeEntity.Name) },
                    { id: PrPayslipSubitemEntity.DecimalValue },
                    { id: PrPayslipSubitemEntity.IntValue },
                ]
            },
        ]
    }];
}

function getPropNameFromType<T extends TPayslipItem>(type: RowValueType): { propName: keyof T; overridePropName?: keyof T } {
    let prop: string;
    switch (type) {
        case RowValueType.Currency:
        case RowValueType.CurrencyPerHour:
        case RowValueType.Percentage:
            prop = "DecimalValue"; break;
        default:
            prop = "IntValue"; break;
    }

    const overridePropName = `${prop}Override` as keyof T;
    const propName = prop as keyof T;
    return {
        overridePropName,
        propName
    };
}

function getFormatterFromType(type: RowValueType, args: ITotalOverviewArgs): (value: number) => string {
    switch (type) {
        case RowValueType.Currency:
            return args.currencyFormatter;
        case RowValueType.CurrencyPerHour:
            return (val: number) => `${args.currencyFormatter(val, 1)}/h`;
        case RowValueType.Percentage:
            return formatPercent;
        case RowValueType.Days:
            return dayFormatter;
        case RowValueType.Hours:
            return hoursFormatter;
        case RowValueType.HoursFromMinutes:
            return minutesToHoursFormatter;
        default:
            return (value) => value.toString();
    }
}

function getUnitFromType(type: RowValueType, args: ITotalOverviewArgs): string {
    switch (type) {
        case RowValueType.Currency:
            return args?.currencyUnit;
        case RowValueType.CurrencyPerHour:
            return `${args?.currencyUnit}/h`;
        case RowValueType.Percentage:
            return "%";
        case RowValueType.Days:
            return "d";
        case RowValueType.Hours:
            return "h";
        case RowValueType.HoursFromMinutes:
            return "min";
    }
}

function getFormTableListAdditionalColumnsFromSubItems(list: TPayslipSubItem[], defs: TPayslipSubItemDef[], args: ITotalOverviewArgs): IFormTableListAdditionalColumn[] {
    const columns: IFormTableListAdditionalColumn[] = [];
    if (!defs?.length) {
        return columns;
    }

    defs.forEach((def, idx) => {
        const [row] = getFormTableListRows(list, def, args);
        columns.push({
            id: isEditableRowDef(def) && !Array.isArray(def.id) ? def.id : `idx-${idx}`,
            // todo: may be custom alignment?
            value: row?.value ?? EMPTY_DASH,
            formatter: row?.formatter
        });
    });
    return columns;
}

function getFromTableListRowFromItemAndDef(itemOrSubItem: TPayslipItem | TPayslipSubItem, def: TPayslipOverviewRowDef, args: ITotalOverviewArgs): IFormTableListRow {
    let value, overrideValue, formatter, editableInfo: IEditableInfoDef;

    // TPayslipSubItem is not fully compatible with TPayslipItem, but we don't use it
    // in incompatible way, so we can cast it
    const item = itemOrSubItem as unknown as TPayslipItem;
    const title = def.label ?? item?.Name ?? item?.Type?.Name;
    const { isHeader, isSubRow } = def;

    if (isEditableRowDef(def)) {
        const valueType = def.valueType ?? RowValueType.Currency;
        const { propName, overridePropName } = getPropNameFromType(valueType);

        overrideValue = item[overridePropName];
        value = (overrideValue ?? item[propName]) as TFormTableListValue;
        formatter = getFormatterFromType(valueType, args);
        editableInfo = {
            Id: item.Id,
            path: args.employment ? `Employments(${args.employment.Id})` : null,
            propName: overridePropName,
            origValue: item[propName] as number,
            unit: getUnitFromType(valueType, args)
        };
    } else {
        // readonly row
        value = def.value;
        // different default valueType for readonly row
        formatter = def.valueType && getFormatterFromType(def.valueType, args);
    }

    return {
        title,
        value,
        formatter: def.formatter ? def.formatter.bind(null, args.item ?? item, formatter) : formatter,
        isMarked: !!overrideValue,
        isHeader,
        isSubRow: isSubRow || def.subItems?.length > 0,
        additionalColumns: getFormTableListAdditionalColumnsFromSubItems(item?.Subitems, def.subItems, { ...args, item }),
        editableInfo
    };
}

function getFormTableListRows<T extends (TPayslipItem | TPayslipSubItem)>(list: T[], rowDef: TPayslipOverviewRowDef, args: ITotalOverviewArgs): IFormTableListRow[] {
    const generatedRows: IFormTableListRow[] = [];

    // we can add additional visibility condition -> check if there are another rows in the list to show this one
    if (rowDef.visibleIfContains) {
        const item = list.find((i) => rowDef.visibleIfContains.includes(i.TypeCode));
        if (!item) {
            return generatedRows;
        }
    }

    if (isEditableRowDef(rowDef)) {
        const types = Array.isArray(rowDef.id) ? rowDef.id : [rowDef.id];
        const filteredItems = list.filter((i) => types.includes(i.TypeCode));

        if (!filteredItems.length) {
            return generatedRows;
        }

        if (rowDef.headerRow) {
            const [headerRow] = getFormTableListRows(list, { ...rowDef.headerRow, isHeader: true, isSubRow: true }, args);
            if (headerRow) {
                generatedRows.push(headerRow);
            }
        }

        filteredItems.forEach(item => {
            generatedRows.push(getFromTableListRowFromItemAndDef(item, rowDef, args));
        });
    } else {
        generatedRows.push(getFromTableListRowFromItemAndDef(null, rowDef, args));
    }

    return generatedRows;
}

interface ITotalOverviewArgs {
    currencyUnit: string;
    currencyFormatter: (value: number, minorUnit?: number) => string;
    item?: TPayslipItem; // subitems need access to parent item, so it can be passed to formatter (additional columns)
    employment?: IPrPayslipEmploymentEntity; // in case of employment item
}

function processRows(rowDefs: TPayslipOverviewRowDef[], items: (IPrPayslipItemEntity | IPrPayslipEmploymentItemEntity)[], args: ITotalOverviewArgs): IFormTableListRow[] {
    const rows: IFormTableListRow[] = [];

    rowDefs.forEach((rowDef) => {
        rows.push(...getFormTableListRows(items, rowDef, args));
    });

    return rows;
}

function createPayslipSections(items: IPrPayslipItemEntity[], args: ITotalOverviewArgs): IFormTableListSection[] {
    const def = getTotalOverviewPayslipSectionsDef();
    return def.map((sectionDef) => {
        return {
            rows: processRows(sectionDef.rows, items, args)
        };
    });
}

function createEmploymentSection(args: ITotalOverviewArgs): IFormTableListSection {
    const def = getTotalOverviewEmploymentSectionsDef();
    return {
        title: getEmploymentName(args.employment),
        rows: processRows(def.rows, args.employment.Items, args)
    };
}

export function getTotalOverviewSections(storage: FormStorage<IPrPayslipEntity>): IFormTableListSection[] {
    // todo: payslip currency
    const currency = getCompanyCurrency(storage.context);

    const { Employments, Items } = storage.data.entity;

    const args: ITotalOverviewArgs = {
        currencyUnit: CurrencyType.getCurrencyUnit(currency),
        currencyFormatter: (value, minorUnits?: number) => formatCurrency(value, currency, minorUnits ?? 0)
    };
    const sections: IFormTableListSection[] = [];

    Employments.forEach((employment => {
        sections.push(createEmploymentSection({ ...args, employment }));
    }));

    sections.push(...createPayslipSections(Items, args));
    return sections.filter((s) => s.rows.length > 0);
}